import type { EquipmentInfoId, ExerciseInfoId, MovementInfoId } from '@az3fr/core';
import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/lib/store';
import { exerciseSliceSelectors } from '@/entities/exercise/model/exercise.selector';
import {
	createNewUserExercise,
	exerciseSliceActions,
} from '@/entities/exercise/model/exercise.slice';

/**
 * Creates a new exercise for a user
 *
 * @todo Server Integration:
 * - Call useCreateExercise mutation (POST /api/exercises)
 * - Handle server response with proper exercise structure
 */
const createNewExercise = createAsyncThunk<
	ExerciseInfoId, // Returns the new exercise ID
	{ movementId: MovementInfoId; equipmentId: EquipmentInfoId },
	{ state: RootState }
>(
	'entities/exercise/createNewExercise',
	// biome-ignore lint/suspicious/useAwait: this is a synchronous operation
	async ({ movementId, equipmentId }, { getState, dispatch, rejectWithValue }) => {
		try {
			const existingExercise = exerciseSliceSelectors.selectByMovementAndEquipment(
				getState(),
				movementId,
				equipmentId,
			);

			// If exercise already exists, return its ID. there is no need to create a new one.
			if (existingExercise) {
				return existingExercise.id;
			}

			const newExercise = createNewUserExercise({ movementId, equipmentId });
			dispatch(exerciseSliceActions.addedOne(newExercise));

			return newExercise.id;
		} catch (error) {
			console.error(error);
			return rejectWithValue(
				`Failed to create new exercise: ${error instanceof Error ? error.message : 'Unknown error'}`,
			);
		}
	},
);

const exerciseEntityThunks = {
	createNewExercise,
};

export { exerciseEntityThunks };
